// src/pages/partners/TrustedPartnersEvcc.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Chip,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Palette / tokens (tuned but same brand feel)
const pageBg = "#f5f7fb";
const sectionHeaderBg = "#e5edc2";
const sectionHeaderBorder = "#9fad3d";
const linkGreen = "#9fad3d";
const darkText = "#111827";
const mutedText = "#4b5563";
const borderColor = "#dde4f0";

function SectionCard({ title, eyebrow, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${borderColor}`,
        mb: 4,
        bgcolor: "#ffffff",
        transition: "transform 150ms ease, box-shadow 150ms ease",
        "&:hover": {
          transform: { md: "translateY(-2px)" },
          boxShadow: { md: "0 10px 30px rgba(15, 23, 42, 0.08)" },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {eyebrow && (
          <Chip
            label={eyebrow}
            size="small"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.3,
              bgcolor: "#fff",
              color: "#4b5563",
            }}
          />
        )}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1d2736", ml: eyebrow ? 1 : 0 }}
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 2.5, sm: 3 } }}>{children}</Box>
    </Paper>
  );
}

export default function TrustedPartnersEvcc() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            mb: 1.5,
          }}
        >
          Home &gt; Trusted Partners &gt; EVCC
        </Typography>

        {/* Hero / page title */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                background:
                  "linear-gradient(135deg, rgba(159,173,61,0.08), rgba(17,24,39,0.03))",
                border: "1px solid rgba(148,163,184,0.4)",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <ElectricBoltIcon sx={{ fontSize: 28, color: linkGreen }} />
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: 1,
                    fontWeight: 700,
                    color: "#6b7280",
                  }}
                >
                  For businesses offering EV charge points
                </Typography>
              </Stack>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: darkText,
                  mb: 1,
                }}
              >
                Trusted Partners – EVCC
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: mutedText,
                  maxWidth: 820,
                }}
              >
                If your business offers EV charge points, you can enhance your RECC
                membership by joining the Electric Vehicle Consumer Code (EVCC). As
                RECC’s sister code, EVCC is designed for businesses in the private EV
                charging sector. It supports legal compliance and aligns with key
                industry standards – like the IET Code of Practice and Building
                Regulations – giving your customers added confidence in your service.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  component="a"
                  href="/assets/partners/evcc/evcc-application-form.pdf" // update to real URL
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    bgcolor: linkGreen,
                    "&:hover": { bgcolor: "#879634" },
                  }}
                >
                  Apply to join EVCC
                </Button>

                <Button
                  variant="text"
                  size="small"
                  component="a"
                  href="/assets/partners/evcc/evcc-requirements.pdf" // update to real URL
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    color: linkGreen,
                    px: 0,
                  }}
                >
                  View EVCC requirements
                </Button>
              </Stack>
            </Paper>
          </Grid>

          {/* Side highlight */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 3,
                height: "100%",
                border: `1px solid ${borderColor}`,
                backgroundColor: "#ffffff",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 1, color: darkText }}
              >
                Why it matters
              </Typography>

              <Typography variant="body2" sx={{ color: mutedText, mb: 2 }}>
                EVCC membership signals that you follow robust consumer protections in a
                fast-growing market – helping you stand out to homeowners, businesses,
                and partners.
              </Typography>

              <List dense sx={{ pl: 0 }}>
                {[
                  "Demonstrate high consumer protection standards.",
                  "Align with recognised industry guidance.",
                  "Reassure customers on quality and after-sales support.",
                ].map((item, idx) => (
                  <ListItem key={idx} sx={{ pl: 0, py: 0.3 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: linkGreen }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: darkText,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Main content sections */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {/* RECC Member Benefit */}
            <SectionCard
              title="RECC member benefit"
              eyebrow="For existing RECC members"
            >
              <Typography variant="body2" sx={{ mb: 1.5, color: mutedText }}>
                As a RECC member, joining EVCC is even more attractive:
              </Typography>

              <List dense sx={{ pl: 0 }}>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="EVCC joining fee waived for all RECC members."
                    primaryTypographyProps={{ variant: "body2", color: darkText }}
                  />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Seamless dual membership demonstrating your commitment to high consumer protection standards, building trust with clients."
                    primaryTypographyProps={{ variant: "body2", color: darkText }}
                  />
                </ListItem>
              </List>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Key reasons to join */}
            <SectionCard title="Key reasons to join EVCC" eyebrow="Business benefits">
              <Typography variant="body2" sx={{ mb: 1.5, color: mutedText }}>
                EVCC membership helps you strengthen your position in the EV charging
                market:
              </Typography>

              <List dense sx={{ pl: 0 }}>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Expand your business to include EV chargepoint installations."
                    primaryTypographyProps={{ variant: "body2", color: darkText }}
                  />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Access free model documents and contracts to support compliant sales and installations."
                    primaryTypographyProps={{ variant: "body2", color: darkText }}
                  />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Receive ongoing technical and regulatory guidance tailored to the EV charging sector."
                    primaryTypographyProps={{ variant: "body2", color: darkText }}
                  />
                </ListItem>
              </List>
            </SectionCard>
          </Grid>

          {/* How to apply – full width */}
          <Grid item xs={12}>
            <SectionCard title="How to apply" eyebrow="Application steps">
              <Typography variant="body2" sx={{ mb: 2, color: mutedText }}>
                Joining EVCC is straightforward:
              </Typography>

              <Box
                component="ol"
                sx={{
                  m: 0,
                  pl: 3,
                  color: darkText,
                  "& li": { mb: 1.5 },
                }}
              >
                <li>
                  <Typography variant="body2">
                    Complete the EVCC application form.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Submit the completed form to{" "}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      [EMAIL ADDRESS]
                    </Box>{" "}
                    along with:
                  </Typography>
                  <List dense sx={{ pl: 2, mt: 0.5 }}>
                    <ListItem sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <ArrowRightIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Evidence of EV chargepoint installation training."
                        primaryTypographyProps={{
                          variant: "body2",
                          color: darkText,
                        }}
                      />
                    </ListItem>
                  </List>
                </li>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: 3 }}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Button
                  variant="contained"
                  component="a"
                  href="/assets/partners/evcc/evcc-application-form.pdf" // update to real URL
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    bgcolor: linkGreen,
                    "&:hover": { bgcolor: "#879634" },
                  }}
                >
                  Click to join
                </Button>
                <Typography
                  variant="body2"
                  sx={{ color: "#6b7280", maxWidth: 420 }}
                >
                  Once we receive your application, the EVCC team will review your
                  details and follow up with any next steps.
                </Typography>
              </Stack>
            </SectionCard>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} md={6}>
            <SectionCard title="Need more information?" eyebrow="Contact EVCC">
              <Typography variant="body2" sx={{ mb: 1.5, color: mutedText }}>
                For more information about joining EVCC, please contact us:
              </Typography>

              <List dense sx={{ pl: 0 }}>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        Telephone:{" "}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          0207 981 0850
                        </Box>
                      </>
                    }
                    primaryTypographyProps={{ variant: "body2", color: darkText }}
                  />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        Email:{" "}
                        <Box
                          component="a"
                          href="mailto:info@electric-vehicle.org.uk"
                          sx={{
                            color: linkGreen,
                            textDecoration: "none",
                            fontWeight: 600,
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          info@electric-vehicle.org.uk
                        </Box>
                      </>
                    }
                    primaryTypographyProps={{ variant: "body2", color: darkText }}
                  />
                </ListItem>
              </List>
            </SectionCard>
          </Grid>

          {/* Optional: small extra panel for external link / FAQ */}
          <Grid item xs={12} md={6}>
            <SectionCard title="Additional resources">
              <Typography variant="body2" sx={{ mb: 1.5, color: mutedText }}>
                You can also explore additional information and updates on EVCC via:
              </Typography>

              <List dense sx={{ pl: 0 }}>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        sx={{
                          color: linkGreen,
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        EVCC website [link to be added]
                      </Box>
                    }
                    primaryTypographyProps={{ variant: "body2", color: darkText }}
                  />
                </ListItem>
              </List>
            </SectionCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
